import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { User, Session } from "@/integrations/mongodb/types";
import { User as AuthUser } from "@/api/authService";
import {
  clientAuthService,
  CurrentUserResult,
} from "@/integrations/mongodb/clientAuthService";
import { SESSION_EXPIRED_EVENT } from "@/api/request";
import { toast } from "sonner";
import { startFacebookLogin } from "@/api/facebookAuth";
import { syncFacebookSenderId } from "@/api/authService";
import { loginWithGoogle } from "@/api/authService";
import { AuthModal } from "@/components/auth/AuthModal";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { unsubscribeFromPushOnSignOut } from "@/lib/pushSubscription";
import { readLastViewedStore } from "@/lib/lastViewedStore";
import { isNewAccountAuth, resolvePostSignupUser } from "@/lib/postSignupUser";
import {
  AUTH_MODAL_TRIGGERS,
  type AuthModalTrigger,
  type SignupContext,
} from "@shared/onboarding";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

// A single deferred retry after a transient /auth/me failure. Long enough for a cold dyno
// or a brief Atlas failover to come back, short enough that the user does not notice.
const SESSION_RETRY_DELAY_MS = 2000;

// Every place that opens the auth modal must declare its intent — captured
// centrally as `auth_modal_shown` so signup conversion can be attributed.
// The union lives in shared/src/onboarding.ts (Phase 49) because the backend uses the same list
// as the signup-context allowlist; add a value THERE when a new surface starts opening the modal.
// Notes on the less obvious values:
//  - chat_limit: the login nudge inside the chat panel's rate-limit row (Phase 38 D-18). Anonymous
//    visitors get a tighter tier, and the wall invites a login for more headroom.
//  - moderator_view: sign-in prompt on the /moderator access gate.
//  - notifications: signed-out visitor landing on /notifications (e.g. a push click after
//    sign-out), Phase 46.
export type { AuthModalTrigger };

// The Facebook flow is a full-page redirect, so the modal's trigger cannot live in a ref across it.
// Saved right before the redirect, read once on return (fb_new branch) and always removed.
const PENDING_SIGNUP_TRIGGER_KEY = "ys_pending_signup_trigger";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userCheckingFinished: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  signUp: ({
    email,
    password,
    fullName,
  }: {
    email: string;
    password: string;
    fullName?: string;
  }) => Promise<{ error?: string }>;
  signIn: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<{ error?: string }>;
  signInWithGoogle: (payload: {
    credential?: string;
    accessToken?: string;
  }) => Promise<{ error?: string }>;
  signInWithFacebook: () => Promise<{ error?: string }>;
  signInWithPhone: (authResponse: {
    user: AuthUser;
    token: string;
    isNewUser?: boolean;
  }) => Promise<{ error?: string }>;
  signOut: () => Promise<{ error?: string }>;
  // storeSlug: the store the modal was opened for (e.g. a store-page review CTA, D-20).
  showAuthModal: (options: {
    trigger: AuthModalTrigger;
    message?: string;
    storeSlug?: string;
  }) => void;
  // The context to send with a signup request (D-04): the modal's trigger and store plus the last
  // viewed store. undefined when the modal was not opened (the backend needs a trigger anyway).
  getSignupContext: () => SignupContext | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultAuthContext: AuthContextType = {
  user: null,
  session: null,
  loading: false,
  userCheckingFinished: true,
  setUser: () => {},
  signUp: async () => ({ error: "SSR" }),
  signIn: async () => ({ error: "SSR" }),
  signInWithGoogle: async () => ({ error: "SSR" }),
  signInWithFacebook: async () => ({ error: "SSR" }),
  signInWithPhone: async () => ({ error: "SSR" }),
  signOut: async () => ({ error: "SSR" }),
  showAuthModal: () => {},
  getSignupContext: () => undefined,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  // On the server, AuthProvider isn't mounted — return safe defaults
  if (context === undefined) {
    if (typeof window === "undefined") {
      return defaultAuthContext;
    }
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (!GOOGLE_CLIENT_ID) {
    console.warn(
      "Google Client ID is not set. Google authentication will not work."
    );
  }
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userCheckingFinished, setUserCheckingFinished] = useState(false);
  const [isAuthModalVisible, setIsAuthModalVisible] = useState(false);
  // Optional context line shown inside the auth modal (e.g. "sign up first to
  // contribute") — set per showAuthModal() call, cleared when the modal closes.
  const [authModalMessage, setAuthModalMessage] = useState<string | undefined>(undefined);
  // The server renders with `defaultAuthContext` (AuthProvider isn't mounted there).
  // To avoid a hydration mismatch, the FIRST client render must expose those same
  // values; we only switch to the real auth state after mount (post-hydration).
  const [hydrated, setHydrated] = useState(false);
  const { identify, trackAuth } = usePostHogTracking();
  // SSR-safe: AuthProvider only mounts client-side, nested under QueryClientProvider
  const queryClient = useQueryClient();
  // True once a session has actually been hydrated in this page life. The session-expiry
  // handler below stays silent unless this is set: a token that was already dead on arrival
  // is initializeAuth's business, and toasting about it on every visit would be noise.
  const hadLiveSessionRef = useRef(false);
  // The intent the auth modal was opened with (D-04). Set by showAuthModal, cleared after a
  // successful sign-in and when the modal closes.
  const pendingSignupContextRef = useRef<{
    trigger: AuthModalTrigger;
    storeSlug?: string;
  } | null>(null);
  // Bumped as the FIRST statement of signOut. A post-signup /auth/me refresh that resolves after a
  // sign-out started (including while push unsubscription is pending) sees a moved generation and
  // applies nothing, so it can never restore a signed-out session (T-49-27).
  const signOutGenerationRef = useRef(0);

  useEffect(() => {
    // Guards for the one deferred retry below: a 2s timer can outlive the provider on a fast
    // navigation, and every setState in the retry path is checked against `cancelled`.
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const clearStoredToken = () => {
      localStorage.removeItem("auth_token");
    };

    // Shared by the first attempt and the retry so the two paths can never drift apart.
    const hydrateSession = (
      hydratedUser: User,
      hydratedToken: string,
      announceFacebookLogin: boolean,
      isNewFacebookAccount = false,
      facebookTrigger?: AuthModalTrigger
    ) => {
      setUser(hydratedUser);
      setSession({ user: hydratedUser, token: hydratedToken });
      hadLiveSessionRef.current = true;
      // Identify user in PostHog
      identify(hydratedUser._id, {
        email: hydratedUser.email,
        full_name: hydratedUser.full_name,
        provider: hydratedUser.provider,
        createdAt: hydratedUser.createdAt,
      });
      if (announceFacebookLogin) {
        // D-03: the callback marks a just-created account with fb_new=1.
        if (isNewFacebookAccount) {
          trackAuth.signUp("facebook", { trigger: facebookTrigger ?? "none" });
        } else {
          trackAuth.signIn("facebook");
        }
        syncUserFacebookSenderIfAvailable({ userId: hydratedUser._id });
        toast.success("تم تسجيل الدخول بنجاح", {
          description: `مرحباً ${
            hydratedUser.full_name || hydratedUser.email
          }`,
        });
      }
    };

    // Exactly one retry, no loop and no backoff ladder — enough to ride out a transient
    // fault without amplifying an outage into a client-driven storm against a recovering dyno.
    const retrySessionHydration = async () => {
      // Re-read the token: a signOut may have cleared it while we waited.
      const retryToken = localStorage.getItem("auth_token");
      if (cancelled || !retryToken) return;

      const retryResult: CurrentUserResult =
        await clientAuthService.getCurrentUser(retryToken);
      if (cancelled) return;

      if (retryResult.status === "authenticated") {
        // No Facebook toast on the retry path — the first attempt already owned that moment.
        hydrateSession(retryResult.user, retryToken, false);
      } else if (retryResult.status === "unauthenticated") {
        clearStoredToken();
      }
      // Still unavailable: give up silently and KEEP the token. The next page load or the
      // user's next action gets another chance.
    };

    const initializeAuth = async () => {
      if (typeof window === "undefined") {
        setLoading(false);
        setUserCheckingFinished(true);
        return;
      }
      try {
        // Handle the Facebook OAuth redirect callback. The backend delivers the
        // JWT (or an error code) in the URL hash: #fb_token=... / #fb_error=... plus
        // #fb_state=<nonce>. We verify the nonce against sessionStorage (CSRF guard),
        // strip the hash immediately, then let the normal token flow below hydrate.
        let justLoggedInWithFacebook = false;
        let isNewFacebookAccount = false;
        let facebookTrigger: AuthModalTrigger | undefined;
        const rawHash = window.location.hash.startsWith("#")
          ? window.location.hash.slice(1)
          : window.location.hash;
        if (rawHash) {
          const hashParams = new URLSearchParams(rawHash);
          const fbToken = hashParams.get("fb_token");
          const fbError = hashParams.get("fb_error");
          const fbState = hashParams.get("fb_state");
          // fb_new rides in the same hash, so the replaceState below strips it with fb_token.
          const fbNew = hashParams.get("fb_new") === "1";

          if (fbToken || fbError) {
            // Read once and always remove (sign-in, signup or error). Validated against the
            // shared allowlist: storage is user-editable.
            try {
              const storedTrigger =
                sessionStorage.getItem("ys_pending_signup_trigger") ||
                localStorage.getItem(PENDING_SIGNUP_TRIGGER_KEY);
              sessionStorage.removeItem(PENDING_SIGNUP_TRIGGER_KEY);
              localStorage.removeItem(PENDING_SIGNUP_TRIGGER_KEY);
              if (
                storedTrigger &&
                (AUTH_MODAL_TRIGGERS as string[]).includes(storedTrigger)
              ) {
                facebookTrigger = storedTrigger as AuthModalTrigger;
              }
            } catch {
              // Storage unavailable: the signup is tracked with trigger "none".
            }
            // Accept the nonce from EITHER storage: localStorage survives the mobile
            // round-trip landing in a fresh tab; the sessionStorage fallback covers a
            // stale cached page that still wrote only sessionStorage (see the matching
            // comment in api/facebookAuth.ts).
            const storedNonce =
              localStorage.getItem("fb_oauth_state") ||
              sessionStorage.getItem("fb_oauth_state");
            localStorage.removeItem("fb_oauth_state");
            sessionStorage.removeItem("fb_oauth_state");
            // Strip the hash so the JWT never lingers in the URL / history
            window.history.replaceState(
              {},
              "",
              window.location.pathname + window.location.search
            );

            if (!storedNonce || storedNonce !== fbState) {
              // CSRF check failed — ignore the token/error entirely
              toast.error("خطأ في تسجيل الدخول", {
                description: "فشل تسجيل الدخول بـ Facebook",
              });
            } else if (fbError) {
              const description =
                fbError === "no_email"
                  ? "لم نتمكن من الحصول على بريدك الإلكتروني من Facebook"
                  : fbError === "cancelled"
                  ? "تم إلغاء تسجيل الدخول عبر Facebook"
                  : "حدث خطأ أثناء تسجيل الدخول بـ Facebook";
              toast.error("خطأ في تسجيل الدخول", { description });
            } else if (fbToken) {
              localStorage.setItem("auth_token", decodeURIComponent(fbToken));
              justLoggedInWithFacebook = true;
              isNewFacebookAccount = fbNew;
            }
          }
        }

        const token = localStorage.getItem("auth_token");
        if (token) {
          const result: CurrentUserResult =
            await clientAuthService.getCurrentUser(token);

          if (result.status === "authenticated") {
            hydrateSession(
              result.user,
              token,
              justLoggedInWithFacebook,
              isNewFacebookAccount,
              facebookTrigger
            );
          } else if (result.status === "unauthenticated") {
            // The server explicitly answered 401 for the session endpoint, so the token
            // really is dead. This is the ONLY removal in this effect.
            clearStoredToken();
          } else {
            // Transient (5xx, network error, the 10s timeout, a malformed 200): KEEP the
            // token, stay unauthenticated for now, and try once more in the background.
            // Fire-and-forget so it never gates the UI — `loading` and
            // `userCheckingFinished` still flip in the `finally` on this first attempt.
            console.warn(
              "Session validation unavailable, keeping token:",
              result.reason
            );
            retryTimer = setTimeout(() => {
              void retrySessionHydration();
            }, SESSION_RETRY_DELAY_MS);
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
        setUserCheckingFinished(true);
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [identify, trackAuth]); // both stable (useCallback / useMemo)

  // The axios interceptor fires SESSION_EXPIRED_EVENT when /auth/me answers 401, and has
  // already removed the token. All that is left is the in-memory teardown: this mirrors
  // signOut minus the navigation and minus the token removal. No reload — a mid-session
  // page jump is exactly what users read as being randomly signed out.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleSessionExpired = () => {
      if (!hadLiveSessionRef.current) return;

      hadLiveSessionRef.current = false;
      setUser(null);
      setSession(null);
      queryClient.clear();
      toast.error("انتهت صلاحية الجلسة", {
        description: "يرجى تسجيل الدخول مرة أخرى",
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [queryClient]);

  // Flip to real auth state only after the first client render has hydrated,
  // keeping SSR HTML and the initial client render identical.
  useEffect(() => {
    setHydrated(true);
  }, []);

  const getSignupContext = (): SignupContext | undefined => {
    const pending = pendingSignupContextRef.current;
    if (!pending) return undefined;
    const lastViewedStoreSlug = readLastViewedStore();
    return {
      trigger: pending.trigger,
      ...(pending.storeSlug ? { storeSlug: pending.storeSlug } : {}),
      ...(lastViewedStoreSlug ? { lastViewedStoreSlug } : {}),
    };
  };

  // After a NEW account (email, Google isNewUser, phone isNewUser), swap in the full /auth/me user
  // so onboardingActive / signupSource / signupContext are present without a reload (D-07, D-14,
  // D-23). Call sites run `await refreshUserAfterSignup(token, user)` AFTER
  // localStorage.setItem("auth_token"): the freshness check compares
  // against the stored token. A failed fetch keeps the signup user; a stale one applies nothing.
  const refreshUserAfterSignup = async (token: string, fallbackUser: User) => {
    const gen = signOutGenerationRef.current;
    const fresh = await resolvePostSignupUser({
      token,
      fallbackUser,
      fetchMe: (t) => clientAuthService.getCurrentUser(t),
      isCurrentToken: (t) =>
        typeof window !== "undefined" &&
        localStorage.getItem("auth_token") === t &&
        signOutGenerationRef.current === gen,
    });
    if (!fresh) return;
    setUser(fresh);
    setSession({ user: fresh, token });
  };

  const signUp = async ({
    email,
    password,
    fullName,
  }: {
    email: string;
    password: string;
    fullName?: string;
  }) => {
    try {
      const signupContext = getSignupContext();
      const result = await clientAuthService.signUp({
        email,
        password,
        fullName,
        signupContext,
      });

      if (result.error) {
        toast.error("خطأ في التسجيل", { description: result.error });
        return { error: result.error };
      }

      if (result.user && result.token) {
        setUser(result.user);
        setSession({ user: result.user, token: result.token });
        localStorage.setItem("auth_token", result.token);
        pendingSignupContextRef.current = null;
        await refreshUserAfterSignup(result.token, result.user);

        // Identify user in PostHog and track signup
        identify(result.user._id, {
          email: result.user.email,
          full_name: result.user.full_name,
          provider: result.user.provider,
          createdAt: result.user.createdAt,
        });
        // PII stays in identify() person properties, not event properties
        trackAuth.signUp("email", { trigger: signupContext?.trigger ?? "none" });

        toast.success("تم إنشاء الحساب بنجاح", { description: "مرحباً بك في المنصة" });
      }

      return {};
    } catch (error) {
      const errorMessage = "حدث خطأ أثناء التسجيل";
      toast.error("خطأ في التسجيل", { description: errorMessage });
      return { error: errorMessage };
    }
  };

  const signIn = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    try {
      const result = await clientAuthService.signIn({ email, password });

      if (result.error) {
        toast.error("خطأ في تسجيل الدخول", { description: result.error });
        return { error: result.error };
      }

      if (result.user && result.token) {
        setUser(result.user);
        setSession({ user: result.user, token: result.token });
        localStorage.setItem("auth_token", result.token);

        identify(result.user._id, {
          email: result.user.email,
          full_name: result.user.full_name,
          provider: result.user.provider,
          createdAt: result.user.createdAt,
        });
        trackAuth.signIn("email");

        toast.success("تم تسجيل الدخول بنجاح", { description: `مرحباً ${result.user.full_name || result.user.email}` });
      }

      return {};
    } catch (error) {
      const errorMessage = "حدث خطأ أثناء تسجيل الدخول";
      toast.error("خطأ في تسجيل الدخول", { description: errorMessage });
      return { error: errorMessage };
    }
  };

  const syncUserFacebookSenderIfAvailable = async ({ userId }) => {
    if (typeof window === "undefined") return;
    const facebookSenderId = localStorage.getItem("facebookSenderId");
    const webSenderId = localStorage.getItem("webSenderId");
    if (facebookSenderId || webSenderId) {
      syncFacebookSenderId({
        facebookSenderId: facebookSenderId || webSenderId,
        userId,
      })
        .then(() => {
          toast.success("تم مزامنة حساب فيسبوك بنجاح");
        })
        .catch((error) => {
          console.error("Error syncing Facebook sender ID:", error);
          toast.error("حدث خطأ أثناء مزامنة حساب فيسبوك");
        })
        .finally(() => {
          window.history.replaceState({}, "", window.location.pathname);
        });
    }
  };
  // Receives the Google token from AuthModal — either an ID token (`credential`)
  // or an OAuth access token (`accessToken`, custom-button useGoogleLogin flow).
  // The button owns the whole Google UI flow, so this only has to exchange the
  // token with our backend, which verifies it server-side.
  const signInWithGoogle = async (payload: {
    credential?: string;
    accessToken?: string;
  }) => {
    try {
      const signupContext = getSignupContext();
      const result = await loginWithGoogle({ ...payload, signupContext });

      if (!result.success || !result.data) {
        const errorMessage =
          result.error?.message || "حدث خطأ أثناء تسجيل الدخول بواسطة Google";
        toast.error("خطأ في تسجيل الدخول", { description: errorMessage });
        return { error: errorMessage };
      }

      const { user: apiUser, token } = result.data;
      // Convert API user to local User type (handle date conversion)
      const user: User = {
        ...apiUser,
        createdAt: new Date(apiUser.createdAt),
        updatedAt: new Date(apiUser.updatedAt),
        provider: apiUser.provider as "email" | "google" | "facebook" | "phone",
      };
      setUser(user);
      setSession({ user, token });
      localStorage.setItem("auth_token", token);
      pendingSignupContextRef.current = null;
      const isNewGoogleAccount = isNewAccountAuth({
        method: "google",
        isNewUser: result.data.isNewUser,
      });
      if (isNewGoogleAccount) {
        await refreshUserAfterSignup(token, user);
      }
      identify(user._id, {
        email: user.email,
        full_name: user.full_name,
        provider: user.provider,
        createdAt: user.createdAt,
      });
      // D-03: a brand-new Google account is a signup, not a sign-in.
      if (isNewGoogleAccount) {
        trackAuth.signUp("google", { trigger: signupContext?.trigger ?? "none" });
      } else {
        trackAuth.signIn("google");
      }
      syncUserFacebookSenderIfAvailable({ userId: user._id });
      return {};
    } catch (error) {
      console.error("Google Sign In error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "حدث خطأ أثناء تسجيل الدخول";
      toast.error("خطأ في تسجيل الدخول", { description: errorMessage });
      return { error: errorMessage };
    }
  };

  const signInWithFacebook = async () => {
    // Full-page redirect to Facebook's OAuth dialog — this navigates away from the
    // page. The result is handled on return via the hash callback in initializeAuth.
    const signupContext = getSignupContext();
    if (typeof window !== "undefined") {
      try {
        // Mirrored to both storages like the OAuth nonce: the mobile round-trip can land in a new
        // tab where sessionStorage is empty. Always overwrite or clear, so a stale trigger from an
        // abandoned attempt is never attributed to a later login.
        if (signupContext) {
          sessionStorage.setItem("ys_pending_signup_trigger", signupContext.trigger);
          localStorage.setItem(PENDING_SIGNUP_TRIGGER_KEY, signupContext.trigger);
        } else {
          sessionStorage.removeItem(PENDING_SIGNUP_TRIGGER_KEY);
          localStorage.removeItem(PENDING_SIGNUP_TRIGGER_KEY);
        }
      } catch {
        // Storage unavailable: the signup is still tracked, with trigger "none".
      }
    }
    startFacebookLogin(signupContext);
    return {};
  };

  const signInWithPhone = async (authResponse: {
    user: AuthUser;
    token: string;
    isNewUser?: boolean;
  }) => {
    try {
      // Captured before anything else: the modal closes (and clears the ref) once sign-in returns.
      const signupContext = getSignupContext();
      const { user: apiUser, token } = authResponse;
      // Convert AuthUser to User type expected by context
      const contextUser: User = {
        _id: apiUser._id,
        email: apiUser.email || apiUser.phoneNumber || "",
        full_name: apiUser.full_name,
        createdAt: new Date(apiUser.createdAt),
        updatedAt: new Date(apiUser.updatedAt),
        provider:
          (apiUser.provider as "email" | "google" | "facebook") || "email",
      };

      setUser(contextUser);
      setSession({ user: contextUser, token });
      localStorage.setItem("auth_token", token);
      pendingSignupContextRef.current = null;
      if (isNewAccountAuth({ method: "phone", isNewUser: authResponse.isNewUser })) {
        await refreshUserAfterSignup(token, contextUser);
      }

      identify(contextUser._id, {
        email: contextUser.email,
        full_name: contextUser.full_name,
        provider: "phone",
        createdAt: contextUser.createdAt,
      });
      if (authResponse.isNewUser) {
        trackAuth.signUp("phone", { trigger: signupContext?.trigger ?? "none" });
      } else {
        trackAuth.signIn("phone");
      }

      toast.success("تم تسجيل الدخول بنجاح", { description: `مرحباً ${apiUser.full_name || apiUser.phoneNumber}` });

      return {};
    } catch (error) {
      const errorMessage = "حدث خطأ أثناء تسجيل الدخول";
      toast.error("خطأ في تسجيل الدخول", { description: errorMessage });
      return { error: errorMessage };
    }
  };

  const signOut = async () => {
    // First statement, before anything is awaited: invalidates any in-flight post-signup refresh.
    // auth_token removal stays AFTER push unsubscription, which authenticates with it.
    signOutGenerationRef.current += 1;
    try {
      // Capture the sign-out event, then posthog.reset() so the next visitor on
      // this device doesn't inherit the previous user's distinct_id.
      trackAuth.signOut();
      setUser(null);
      setSession(null);
      hadLiveSessionRef.current = false;
      // Drop this device's push subscription while the token still exists (the remove route is
      // authenticated), so a shared browser stops receiving this user's pushes. Bounded and
      // never throws: sign-out must not hang on a stuck service worker or network.
      await unsubscribeFromPushOnSignOut();
      localStorage.removeItem("auth_token");
      // Clear cached user-scoped queries (e.g. ["merchant","my-claims"]) so the
      // merchant dashboard link disappears from the Header without a page refresh
      queryClient.clear();

      toast.success("تم تسجيل الخروج", { description: "نراك قريباً" });

      return {};
    } catch (error) {
      const errorMessage = "حدث خطأ أثناء تسجيل الخروج";
      toast.error("خطأ في تسجيل الخروج", { description: errorMessage });
      return { error: errorMessage };
    }
  };

  const showAuthModal = (options: {
    trigger: AuthModalTrigger;
    message?: string;
    storeSlug?: string;
  }) => {
    pendingSignupContextRef.current = {
      trigger: options.trigger,
      ...(options.storeSlug ? { storeSlug: options.storeSlug } : {}),
    };
    trackAuth.authModalShown(options.trigger);
    setAuthModalMessage(options?.message);
    setIsAuthModalVisible(true);
  };

  const value = {
    // Pre-hydration, mirror the server's defaultAuthContext so the first client
    // render matches the SSR HTML; real values take over once `hydrated` is true.
    user: hydrated ? user : defaultAuthContext.user,
    session: hydrated ? session : defaultAuthContext.session,
    loading: hydrated ? loading : defaultAuthContext.loading,
    userCheckingFinished: hydrated
      ? userCheckingFinished
      : defaultAuthContext.userCheckingFinished,
    setUser,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signInWithPhone,
    signOut,
    showAuthModal,
    getSignupContext,
  };

  return (
    <AuthContext.Provider value={value}>
      <AuthModal
        isOpen={isAuthModalVisible}
        message={authModalMessage}
        onClose={() => {
          // Closed with or without auth: either way the intent is spent. Every sign-in path
          // reads getSignupContext() before the modal can close.
          pendingSignupContextRef.current = null;
          setIsAuthModalVisible(false);
          setAuthModalMessage(undefined);
        }}
      />
      {children}
    </AuthContext.Provider>
  );
};
